import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CheckCheckIcon,
  CheckCircle2Icon,
  CheckIcon,
  SearchIcon,
  ShieldCheckIcon,
  XIcon,
  ZapIcon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDateLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { useShopQuery, useVerifyShop } from "../api/queries";

function shopCode(id: string): string {
  const short = id.replace(/-/g, "").slice(-8).toUpperCase();
  return `SH-${short}`;
}

function profileCompletion(shop: {
  name?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
}): number {
  let filled = 0;
  if (shop.name?.trim()) filled++;
  if (shop.phone?.trim()) filled++;
  if (shop.address?.trim()) filled++;
  if (shop.city?.trim()) filled++;
  return Math.round((filled / 4) * 100);
}

export function ShopVerifyPage() {
  const nav = useNavigate({ from: "/app/shops/$id/verify" });
  const { id } = useParams({ from: "/app/shops/$id/verify" });
  const [showSuccess, setShowSuccess] = useState(false);

  const { data: shop, ...result } = useShopQuery(id ?? "");
  const verifyShop = useVerifyShop(id ?? "");

  const completion = shop ? profileCompletion(shop) : 0;
  const branchCount = shop?.branches?.length ?? 0;
  const hasOwner = shop?.staff?.some((s) => s.staffRole === "owner") ?? false;
  const basicMet = completion >= 100 && branchCount >= 1 && hasOwner;
  const currentLevel = shop?.verifiedLevel ?? "none";
  const canUpgradeToBasic = basicMet && currentLevel === "none";
  const canUpgradeToVerified = basicMet && currentLevel === "basic";
  const contactPerson =
    shop?.staff?.find((s) => s.staffRole === "owner")?.userName ?? "-";

  const handleUpgradeToBasic = async () => {
    try {
      await verifyShop.mutateAsync("basic");
      toast.success("ອັບເກຣດເປັນ Basic ສຳເລັດ");
      setShowSuccess(true);
    } catch {
      toast.error("ບໍ່ສາມາດອັບເກຣດໄດ້");
    }
  };

  const handleUpgradeToVerified = async () => {
    try {
      await verifyShop.mutateAsync("verified");
      toast.success("ອັບເກຣດເປັນ Verified ສຳເລັດ", {
        description: "ຮ້ານຖືກສົ່ງໄປກວດສອບແລ້ວ",
      });
      setShowSuccess(true);
    } catch {
      toast.error("ບໍ່ສາມາດອັບເກຣດໄດ້");
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຮ້ານຄ້າ", to: "/app/shops" },
            { label: "ຢືນຢັນຮ້ານ" },
          ]}
        />
      </Header>

      <Main>
        {showSuccess && (
          <Alert className="mb-4 border-success/50 bg-success/10">
            <CheckCircle2Icon className="size-4 text-success" />
            <AlertDescription>
              ປັບປຸງໂປຣໄຟລ໌ຮ້ານສຳເລັດແລ້ວ ສາມາດກວດສອບເອກະສານໄດ້ແລ້ວ
            </AlertDescription>
          </Alert>
        )}

        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              VERIFY SHOP:{" "}
              <span className="text-secondary">
                {shop?.name ?? "ກຳລັງໂຫຼດ..."}
              </span>
            </h2>
            <p className="text-muted-foreground">
              ລະບົບກວດສອບ ແລະ ຢືນຢັນຂໍ້ມູນຮ້ານຄ້າ B2B
            </p>
          </div>
          <Button variant="outline" onClick={() => nav({ to: "/app/shops" })}>
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນລາຍການ
          </Button>
        </div>

        <QueryState result={result} isEmpty={!shop}>
          {shop && (
            <>
              {/* Stepper */}
              <div className="mb-8 flex items-center justify-center gap-2">
                <div
                  className={`flex flex-col items-center gap-1 ${
                    currentLevel === "none"
                      ? "text-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  <div
                    className={`flex size-10 items-center justify-center rounded-full border-2 ${
                      currentLevel === "none"
                        ? "border-secondary bg-secondary/20"
                        : "border-muted"
                    }`}
                  >
                    <span className="size-4 rounded-full bg-secondary" />
                  </div>
                </div>
                <div className="h-0.5 w-12 bg-muted" />
                <div
                  className={`flex flex-col items-center gap-1 ${
                    currentLevel === "basic"
                      ? "text-secondary"
                      : "text-muted-foreground"
                  }`}
                >
                  <div
                    className={`flex size-10 items-center justify-center rounded-full border-2 ${
                      currentLevel === "basic"
                        ? "border-secondary bg-secondary/20"
                        : currentLevel === "verified"
                          ? "border-secondary bg-secondary/20"
                          : "border-muted"
                    }`}
                  >
                    <ShieldCheckIcon className="size-5" />
                  </div>
                  <span className="font-medium text-xs">BASIC STATUS</span>
                </div>
                <div className="h-0.5 w-12 bg-muted" />
                <div
                  className={`flex flex-col items-center gap-1 ${
                    currentLevel === "verified"
                      ? "text-primary"
                      : "text-muted-foreground"
                  }`}
                >
                  <div
                    className={`flex size-10 items-center justify-center rounded-full border-2 ${
                      currentLevel === "verified"
                        ? "border-primary bg-primary/20"
                        : "border-muted"
                    }`}
                  >
                    <SearchIcon className="size-5" />
                  </div>
                  <span className="font-medium text-xs">VERIFIED</span>
                </div>
              </div>

              {/* Criteria Cards */}
              <div className="mb-8 grid gap-6 md:grid-cols-2">
                {/* Basic Criteria - Success = Tech Blue (secondary) */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="font-medium text-secondary text-sm uppercase tracking-wide">
                      BASIC CRITERIA
                    </CardTitle>
                    {basicMet ? (
                      <div className="flex items-center gap-1 text-success">
                        <CheckIcon className="size-5" />
                        <span className="font-medium text-xs">
                          REQUIREMENT MET
                        </span>
                      </div>
                    ) : null}
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between rounded-md bg-muted px-2 py-2">
                      <div className="flex items-center gap-2">
                        {completion >= 100 ? (
                          <CheckCheckIcon className="size-4 text-success" />
                        ) : (
                          <XIcon className="size-4 text-muted-foreground" />
                        )}
                        <span>ຄວາມສົມບູນແບບ</span>
                      </div>
                      <Badge
                        variant={completion >= 100 ? "success" : "outline"}
                      >
                        {completion >= 100
                          ? `${completion}%`
                          : `${completion}% ຍັງບໍ່ຜ່ານ`}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between rounded-md bg-muted px-2 py-2">
                      <div className="flex items-center gap-2">
                        {branchCount >= 1 ? (
                          <CheckCheckIcon className="size-4 text-success" />
                        ) : (
                          <XIcon className="size-4 text-muted-foreground" />
                        )}
                        <span>1+ ສາຂາລົງທະບຽນ</span>
                      </div>
                      <Badge variant={branchCount >= 1 ? "success" : "outline"}>
                        {branchCount >= 1 ? `${branchCount} ສາຂາ` : "ຍັງບໍ່ຜ່ານ"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between rounded-md bg-muted px-2 py-2">
                      <div className="flex items-center gap-2">
                        {hasOwner ? (
                          <CheckCheckIcon className="size-4 text-success" />
                        ) : (
                          <XIcon className="size-4 text-muted-foreground" />
                        )}
                        <span>1+ ເຈົ້າຂອງຮ້ານ</span>
                      </div>
                      <Badge variant={hasOwner ? "success" : "outline"}>
                        {hasOwner ? "ຢືນຢັນແລ້ວ" : "ຍັງບໍ່ຜ່ານ"}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Verified Criteria - โครงเหมือน Basic Criteria */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="font-medium text-secondary text-sm uppercase tracking-wide">
                      VERIFIED CRITERIA
                    </CardTitle>
                    {currentLevel === "verified" ? (
                      <div className="flex items-center gap-1 text-success">
                        <CheckIcon className="size-5" />
                        <span className="font-medium text-xs">APPROVED</span>
                      </div>
                    ) : null}
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between rounded-md bg-muted px-2 py-2">
                      <div className="flex items-center gap-2">
                        {currentLevel === "verified" ? (
                          <CheckCheckIcon className="size-4 text-success" />
                        ) : (
                          <XIcon className="size-4 text-muted-foreground" />
                        )}
                        <span>ການອະນຸມັດຈາກ Admin</span>
                      </div>
                      <Badge
                        variant={
                          currentLevel === "verified" ? "success" : "outline"
                        }
                      >
                        {currentLevel === "verified" ? "ອະນຸມັດແລ້ວ" : "ຍັງບໍ່ຜ່ານ"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between rounded-md bg-muted px-2 py-2">
                      <div className="flex items-center gap-2">
                        {currentLevel === "verified" ? (
                          <CheckCheckIcon className="size-4 text-success" />
                        ) : (
                          <XIcon className="size-4 text-muted-foreground" />
                        )}
                        <span>ການກວດສອບເອກະສານ</span>
                      </div>
                      <Badge
                        variant={
                          currentLevel === "verified" ? "success" : "outline"
                        }
                      >
                        {currentLevel === "verified" ? "ສຳເລັດ" : "ຍັງບໍ່ຜ່ານ"}
                      </Badge>
                    </div>
                    {canUpgradeToBasic && (
                      <Button
                        className="mt-4 w-full"
                        onClick={handleUpgradeToBasic}
                        disabled={verifyShop.isPending}
                      >
                        <ShieldCheckIcon className="size-4" />
                        ອັບເກຣດເປັນ BASIC
                      </Button>
                    )}
                    {canUpgradeToVerified && (
                      <>
                        <Button
                          className="mt-4 w-full"
                          onClick={handleUpgradeToVerified}
                          disabled={verifyShop.isPending}
                        >
                          <ZapIcon className="size-4" />
                          ອັບເກຣດເປັນ VERIFIED
                        </Button>
                        <p className="text-center text-muted-foreground text-xs">
                          ການກົດປຸ່ມຈະເປີດໃຫ້ທີມ A1 Compliance ກວດສອບເອກະສານ
                        </p>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Shop Info Cards */}
              <div className="grid gap-4 sm:grid-cols-3">
                <Card className="gap-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-medium text-muted-foreground text-xs uppercase">
                      ລະຫັດຮ້ານ
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium font-mono">{shopCode(shop.id)}</p>
                  </CardContent>
                </Card>
                <Card className="gap-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-medium text-muted-foreground text-xs uppercase">
                      ວັນທີລົງທະບຽນ
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium">
                      {formatDateLocal(shop.createdAt)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="gap-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-medium text-muted-foreground text-xs uppercase">
                      ຜູ້ຕິດຕໍ່
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium">{contactPerson}</p>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </QueryState>
      </Main>
    </>
  );
}
